import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiX, FiChevronLeft, FiChevronRight, FiCamera } from 'react-icons/fi';
import img1 from "../../images/tracy-alternative-(1).jpg";
import img2 from "../../images/AE-service-img-2.jpeg";
import img3 from "../../images/AE-service-img-3.jpeg";
import img5 from "../../images/AE-img-5.jpeg";
import img7 from "../../images/AE-team-1.jpeg";
import img8 from "../../images/AE-team-2.jpeg";
import img9 from "../../images/AE-team-3.jpeg";
import img10 from "../../images/AE-team-4.jpeg";
import img11 from "../../images/AE-team-5.jpeg";
import img12 from "../../images/AE-team-6.jpeg";
import img13 from "../../images/AE-team-7.jpeg";
import img14 from "../../images/AE-team-8.jpeg";
import Logo from "../../images/AE-Hero.svg";

const albumPhotos = [
  { id: 1, src: img7, caption: 'Solar installation at a residential site in Mbabane', rowSpan: 4, objectPosition: 'center 20%' },
  { id: 2, src: img8, caption: 'Electrical panel upgrade — commercial project', rowSpan: 3, colSpan: 2, objectPosition: 'center 15%' },
  { id: 3, src: img9, caption: 'Energy monitoring system commissioning', rowSpan: 2, colSpan: 2, objectPosition: 'center 20%' },
  { id: 4, src: img10, caption: 'Project handover — Manzini industrial client', objectPosition: 'center 20%' },
  { id: 5, src: img11, caption: 'Generator maintenance service in progress', colSpan: 2, objectPosition: 'center 20%' },
  { id: 6, src: img14, caption: 'Solar panel cleaning and inspection', objectPosition: 'center 20%' },
  { id: 7, src: img12, caption: 'Solar panel cleaning and inspection', rowSpan: 2, colSpan: 2, objectPosition: 'center 20%' },
  { id: 8, src: img13, caption: 'Solar panel cleaning and inspection', rowSpan: 2, objectPosition: 'center 35%' },
];

const teamMembers = [
  {
    id: 1,
    name: 'Denzil Young',
    role: 'CFO',
    bio: 'Drives the financial strategy and sustainability of Alternative Energy, overseeing budgeting, investment planning, and fiscal governance to support long-term growth across Eswatini.',
    image: img2,
  },
  {
    id: 2,
    name: 'Tasmia Young',
    role: 'CEO',
    bio: 'Leads Alternative Energy with a vision for accessible, reliable clean energy solutions. Committed to expanding solar, electrical, and generator services that empower homes and businesses across Eswatini.',
    image: img3,
  },
  {
    id: 3,
    name: 'Bongani Dvuba',
    role: 'Project Manager',
    bio: 'Ensures every installation is delivered on time, on budget, and exceeds expectations.',
    image: img5,
  },
  {
    id: 4,
    name: 'Tracy Forbes',
    role: 'Administrator',
    bio: 'Keeps operations running smoothly — managing scheduling, client communications, and administrative processes that ensure every customer experience is seamless from first contact to project completion.',
    image: img1,
  },
];

// ─── Photo Album Modal ────────────────────────────────────────────────────────
const PhotoAlbum = ({ onClose }) => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 640);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const prev = () => setActiveIndex((i) => (i - 1 + albumPhotos.length) % albumPhotos.length);
  const next = () => setActiveIndex((i) => (i + 1) % albumPhotos.length);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') activeIndex !== null ? setActiveIndex(null) : onClose();
      if (e.key === 'ArrowLeft' && activeIndex !== null) prev();
      if (e.key === 'ArrowRight' && activeIndex !== null) next();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [activeIndex, onClose]);

  return createPortal(
    <>
      {/* ── Album backdrop ── */}
      <div className="ae-album-backdrop" onClick={onClose}>
        <div className="ae-album-panel" onClick={(e) => e.stopPropagation()}>

          {/* Header */}
          <div className="ae-album-header">
            <div className="ae-album-header-left">
              <div className="ae-album-icon-wrap">
                <FiCamera />
              </div>
              <div>
                <p className="ae-album-title">Our Photo Album</p>
                <p className="ae-album-subtitle">{albumPhotos.length} photos from the field</p>
              </div>
            </div>
            <button className="ae-album-close" onClick={onClose} aria-label="Close album">
              <FiX />
            </button>
          </div>

          {/* Thumbnail grid */}
          <div className="ae-album-grid-wrap">
            <div className="ae-album-grid">
              {albumPhotos.map((photo, index) => (
                <button
                  key={photo.id}
                  className="ae-thumb-btn"
                  style={isMobile ? {} : {
                    ...(photo.rowSpan ? { gridRow: `span ${photo.rowSpan}` } : {}),
                    ...(photo.colSpan ? { gridColumn: `span ${photo.colSpan}` } : {}),
                  }}
                  onClick={() => setActiveIndex(index)}
                  aria-label={`View photo: ${photo.caption}`}
                >
                  <img
                    src={photo.src}
                    alt={photo.caption}
                    className="ae-thumb-img"
                    loading="lazy"
                    style={{ objectPosition: photo.objectPosition || 'center top' }}
                  />
                  <div className="ae-thumb-overlay">
                    <p className="ae-thumb-caption">{photo.caption}</p>
                  </div>
                  <span className="ae-thumb-index">{String(index + 1).padStart(2, '0')}</span>
                </button>
              ))}
            </div>
          </div>

        </div>
      </div>

      {/* ── Lightbox ── */}
      {activeIndex !== null && (
        <div className="ae-lightbox-backdrop" onClick={() => setActiveIndex(null)}>
          <div className="ae-lightbox-inner" onClick={(e) => e.stopPropagation()}>

            <button className="ae-lb-close" onClick={() => setActiveIndex(null)} aria-label="Close lightbox">
              <FiX />
            </button>

            <img
              src={albumPhotos[activeIndex].src}
              alt={albumPhotos[activeIndex].caption}
              className="ae-lightbox-img"
            />
            <p className="ae-lightbox-caption">{albumPhotos[activeIndex].caption}</p>

            <span className="ae-lb-counter">{activeIndex + 1} / {albumPhotos.length}</span>

            <button
              className="ae-lb-nav ae-lb-prev"
              onClick={(e) => { e.stopPropagation(); prev(); }}
              aria-label="Previous photo"
            >
              <FiChevronLeft />
            </button>

            <button
              className="ae-lb-nav ae-lb-next"
              onClick={(e) => { e.stopPropagation(); next(); }}
              aria-label="Next photo"
            >
              <FiChevronRight />
            </button>

          </div>
        </div>
      )}
    </>,
    document.body
  );
};

// ─── Team Section ─────────────────────────────────────────────────────────────
const Team = () => {
  const [albumOpen, setAlbumOpen] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const AOS = require('aos');
      AOS.refresh();
    }
  }, []);

  const handleCloseAlbum = useCallback(() => setAlbumOpen(false), []);

  useEffect(() => {
    document.body.style.overflow = albumOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [albumOpen]);

  return (
    <section id="team" className="team section-bg py-16 bg-gray-50 relative">

      {/* Logo — top-right */}
      <div className="absolute top-6 right-6 z-10">
        <img src={Logo} alt="Alternative Energy" className="h-10 w-auto md:h-14 opacity-80" />
      </div>

      <div className="container mx-auto px-4" data-aos="fade-up">

        {/* Section title */}
        <div className="section-title text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-4">Our Team</h2>
          <div className="flex justify-center mb-4">
            <div className="h-1 w-16 rounded-full" style={{ background: 'var(--accent-color)' }} />
            <div className="h-1 w-6 rounded-full mx-1 opacity-40" style={{ background: 'var(--accent-color)' }} />
            <div className="h-1 w-3 rounded-full opacity-20" style={{ background: 'var(--accent-color)' }} />
          </div>
          <p className="text-gray-500 max-w-2xl mx-auto">
            Meet the dedicated professionals behind Alternative Energy — certified specialists committed to
            delivering integrated solar, electrical, and generator solutions across Eswatini.
          </p>
        </div>

        {/* Team grid */}
        <div className="flex flex-wrap -mx-4 items-stretch">
          {teamMembers.map((member, index) => (
            <div
              key={member.id}
              className="w-full lg:w-1/2 px-4 mb-8 flex"
              data-aos="zoom-in"
              data-aos-delay={`${(index + 1) * 100}`}
            >
              <div className="member flex flex-col sm:flex-row items-center sm:items-start bg-white rounded-xl shadow-md p-5 gap-5 hover:shadow-lg transition-shadow duration-300 w-full">
                <div className="pic ae-member-avatar flex-shrink-0 ring-2 ring-orange-200">
                  <img
                    src={member.image}
                    className="w-full h-full object-cover object-top block"
                    alt={member.name}
                  />
                </div>
                <div className="member-info flex-1 min-w-0 text-center sm:text-left">
                  <h4 className="text-lg font-bold text-gray-800 mb-0.5 leading-snug">{member.name}</h4>
                  <span className="text-orange-500 font-medium text-sm block mb-2">{member.role}</span>
                  <p className="text-gray-500 text-sm leading-relaxed break-words">{member.bio}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Photo Album trigger — centered below team grid */}
      <div className="text-center mt-4 mb-8">
        <button
          onClick={() => setAlbumOpen(true)}
          className="inline-flex items-center gap-2 px-6 py-2.5 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition-colors duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
        >
          <FiCamera className="text-lg" />
          View Our Photo Album
        </button>
      </div>

      {albumOpen && <PhotoAlbum onClose={handleCloseAlbum} />}
    </section>
  );
};

export default Team;